// BPS-account API client. Authentication happens via the cross-app handoff
// "Bps" session cookie (magic-link local account issued by bps-corp.com — the
// same flow AskBI uses), which the browser sends automatically with
// credentials:'include'. On 401 we bounce the user through /AuthBridge/Login.

const BASE = process.env.REACT_APP_API_URL || '';

async function apiFetch(path, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (res.status === 401) {
    // Session expired or user not signed in — bounce through the BPS handoff
    // (lands on the bps-corp.com magic-link sign-in, then back here).
    window.location.href = '/AuthBridge/Login?returnUrl=' +
      encodeURIComponent(window.location.pathname);
    return;
  }

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

export const api = {
  // Projects
  getProjects:    ()       => apiFetch('/api/projects'),
  createProject:  (body)   => apiFetch('/api/projects', { method: 'POST', body: JSON.stringify(body) }),
  updateProject:  (id, b)  => apiFetch(`/api/projects/${id}`, { method: 'PUT',  body: JSON.stringify(b) }),
  deleteProject:  (id)     => apiFetch(`/api/projects/${id}`, { method: 'DELETE' }),

  // Entries
  getEntries:  (from, to)  => apiFetch(`/api/entries?from=${from}&to=${to}`),
  createEntry: (body)      => apiFetch('/api/entries', { method: 'POST', body: JSON.stringify(body) }),
  updateEntry: (id, body)  => apiFetch(`/api/entries/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteEntry: (id)        => apiFetch(`/api/entries/${id}`, { method: 'DELETE' }),
  duplicateWeek: (source_week_start) =>
    apiFetch('/api/entries/duplicate-week', {
      method: 'POST',
      body:   JSON.stringify({ source_week_start }),
    }),

  // Report
  getReport: (from, to, project_id) => {
    const qs = new URLSearchParams({ from, to });
    if (project_id) qs.set('project_id', project_id);
    return apiFetch(`/api/entries/report?${qs}`);
  },
  getEarningsSummary: (scope) => {
    const qs = new URLSearchParams();
    if (scope && scope !== 'all') qs.set('scope', scope);
    const q = qs.toString();
    return apiFetch(`/api/entries/earnings-summary${q ? '?' + q : ''}`);
  },

  // Invoices
  getInvoices: (project_id, openOnly) => {
    const qs = new URLSearchParams();
    if (project_id) qs.set('project_id', project_id);
    if (openOnly)   qs.set('open', 'true');
    return apiFetch(`/api/invoices?${qs}`);
  },
  createInvoice: (body)     => apiFetch('/api/invoices', { method: 'POST', body: JSON.stringify(body) }),
  updateInvoice: (id, body) => apiFetch(`/api/invoices/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteInvoice: (id)       => apiFetch(`/api/invoices/${id}`, { method: 'DELETE' }),

  // Open items
  getOpenItems: (project_id, openOnly) => {
    const qs = new URLSearchParams();
    if (project_id) qs.set('project_id', project_id);
    if (openOnly)   qs.set('open', 'true');
    return apiFetch(`/api/open-items?${qs}`);
  },
  createOpenItem: (body)     => apiFetch('/api/open-items', { method: 'POST', body: JSON.stringify(body) }),
  updateOpenItem: (id, body) => apiFetch(`/api/open-items/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteOpenItem: (id)       => apiFetch(`/api/open-items/${id}`, { method: 'DELETE' }),

  // PDF template
  getPdfTemplate: () => apiFetch('/api/pdf-template'),

  // Upwork import
  importUpworkParse:  (csv)  => apiFetch('/api/import/upwork/parse',  { method: 'POST', body: JSON.stringify({ csv }) }),
  importUpworkCommit: (body) => apiFetch('/api/import/upwork/commit', { method: 'POST', body: JSON.stringify(body) }),

  // Upwork transactions (dashboard)
  importUpworkTxn: (csv)    => apiFetch('/api/upwork-txn/import', { method: 'POST', body: JSON.stringify({ csv }) }),
  clearUpworkTxn:  (count)  => apiFetch('/api/upwork-txn/clear', {
    method: 'DELETE',
    body:   JSON.stringify({ confirm: count }),
  }),
  countUpworkTxn:     ()         => apiFetch('/api/upwork-txn/count'),
  getUpworkDashboard: (period)   => apiFetch(`/api/upwork-txn/dashboard?period=${period || '12mo'}`),

  // Upwork OAuth connection (per-user). Connect is a full-page redirect (the
  // server 302s to Upwork's consent screen), so it's a navigation, not a fetch.
  getUpworkStatus:  ()  => apiFetch('/api/upwork/status'),
  disconnectUpwork: ()  => apiFetch('/api/upwork/disconnect', { method: 'POST' }),
  connectUpworkUrl: '/api/upwork/connect',
};
